import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { ListPagination } from "../components/ListPagination";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import {
  BillwerkCustomerMatchDocument,
  BillwerkCustomerMatchGroup,
  type BillwerkCustomerMatchQuery,
  type BillwerkCustomerMatchQueryVariables,
  BillwerkCustomerMatchReason,
  BillwerkListStatus,
  CrmObject,
} from "../generated/graphql";
import { crmRecordPath, formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { typography } from "../theme/designTokens";

type Match = BillwerkCustomerMatchQuery["billwerkCustomerMatch"];
type Row = Match["rows"][number];
type Account = Row["accounts"][number];
type Customer = Row["customers"][number];

// The server's page size.
const PAGE_SIZE = 50;
const POLL_MS = 5000;
const SEARCH_DEBOUNCE_MS = 300;

const GROUPS = [
  BillwerkCustomerMatchGroup.Matched,
  BillwerkCustomerMatchGroup.OnlyInSalesforce,
  BillwerkCustomerMatchGroup.OnlyInBillwerk,
  BillwerkCustomerMatchGroup.Conflict,
];

const isGroup = (value: string | null): value is BillwerkCustomerMatchGroup =>
  GROUPS.includes(value as BillwerkCustomerMatchGroup);

const isReason = (value: string | null): value is BillwerkCustomerMatchReason =>
  Object.values(BillwerkCustomerMatchReason).includes(value as BillwerkCustomerMatchReason);

const AccountCell = ({ account }: { account: Account }) => {
  const translate = useTranslate();
  return (
    <Box>
      <Link component={RouterLink} to={crmRecordPath(CrmObject.Account, account.salesforceId)}>
        {account.name ?? translate("billwerkCustomerMatch.noName")}
      </Link>
      <Typography variant="body2" color="text.secondary">
        {account.salesforceId}
      </Typography>
      {account.oldCustomerNumber && (
        <Typography variant="body2" color="text.secondary">
          {translate("billwerkCustomerMatch.oldNumber", { number: account.oldCustomerNumber })}
        </Typography>
      )}
      <Stack direction="row" gap={0.5} mt={0.5}>
        {account.b2c && <Chip size="small" label={translate("billwerkCustomerMatch.b2c")} />}
        {account.billed && <Chip size="small" variant="outlined" label={translate("billwerkCustomerMatch.billed")} />}
      </Stack>
    </Box>
  );
};

const CustomerCell = ({ customer }: { customer: Customer }) => {
  const translate = useTranslate();
  const name = customer.name ?? translate("billwerkCustomerMatch.noName");
  const contract =
    customer.activeContract === null
      ? "billwerkCustomerMatch.contractUnknown"
      : customer.activeContract
        ? "billwerkCustomerMatch.activeContract"
        : "billwerkCustomerMatch.noActiveContract";
  return (
    <Box>
      {customer.url ? (
        <Link href={customer.url} target="_blank" rel="noopener noreferrer">
          {name}
        </Link>
      ) : (
        <Typography>{name}</Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {customer.id}
      </Typography>
      {customer.debitorAccount && (
        <Typography variant="body2" color="text.secondary">
          {translate("billwerkCustomerMatch.columns.debitorAccount")}: {customer.debitorAccount}
        </Typography>
      )}
      {customer.oldCustomerNumber && (
        <Typography variant="body2" color="text.secondary">
          {translate("billwerkCustomerMatch.oldNumber", { number: customer.oldCustomerNumber })}
        </Typography>
      )}
      {customer.externalCustomerId && (
        <Typography variant="body2" color="text.secondary">
          {translate("billwerkCustomerMatch.externalId", { id: customer.externalCustomerId })}
        </Typography>
      )}
      <Chip size="small" variant="outlined" sx={{ mt: 0.5 }} label={translate(contract)} />
    </Box>
  );
};

const MatchRow = ({ row }: { row: Row }) => {
  const translate = useTranslate();
  const chainBroken =
    row.reason === BillwerkCustomerMatchReason.PayerLoop || row.reason === BillwerkCustomerMatchReason.PayerNotFound;
  return (
    <TableRow data-testid="billwerk-match-row">
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack gap={1}>
          {row.accounts.map(account => (
            <AccountCell key={account.salesforceId} account={account} />
          ))}
          {row.payer && (
            <Typography variant="body2">
              {translate(chainBroken ? "billwerkCustomerMatch.chainEndsAt" : "billwerkCustomerMatch.paidBy", {
                name: row.payer.name ?? row.payer.salesforceId,
              })}
            </Typography>
          )}
          {row.billedFacilities > 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("billwerkCustomerMatch.billedFacilities", { smart_count: row.billedFacilities })}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>{row.debitorAccount ?? "–"}</TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack gap={1}>
          {row.customers.map(customer => (
            <CustomerCell key={customer.id} customer={customer} />
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {row.reason && translate(`billwerkCustomerMatch.reasons.${row.reason}`)}
        {row.mappedCustomerIds.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate("billwerkCustomerMatch.mapped", { ids: row.mappedCustomerIds.join(", ") })}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const countOf = (match: Match, group: BillwerkCustomerMatchGroup, reason?: BillwerkCustomerMatchReason): number =>
  match.counts
    .filter(count => count.group === group && (!reason || count.reason === reason))
    .reduce((sum, count) => sum + count.count, 0);

const BillwerkCustomerMatchView = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const groupParam = params.get("group");
  const group = isGroup(groupParam) ? groupParam : BillwerkCustomerMatchGroup.Matched;
  const reasonParam = params.get("reason");
  const reason = isReason(reasonParam) ? reasonParam : null;
  const search = params.get("q") ?? "";
  const page = Math.max(0, Number(params.get("page")) || 0);
  const [searchInput, setSearchInput] = useState(search);
  const [refreshAfter, setRefreshAfter] = useState<string | null>(null);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)));
        return next;
      },
      { replace: true }
    );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) update({ q: searchInput.trim() || null, page: null });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // Only the typed text starts a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const variables: BillwerkCustomerMatchQueryVariables = {
    group,
    reason,
    search: search || null,
    offset: page * PAGE_SIZE,
    refreshAfter,
  };
  const { data, loading, error, startPolling, stopPolling } = useQuery<
    BillwerkCustomerMatchQuery,
    BillwerkCustomerMatchQueryVariables
  >(BillwerkCustomerMatchDocument, { variables, fetchPolicy: "cache-and-network" });
  const match = data?.billwerkCustomerMatch;
  const status = match?.billwerk.status;
  const pending = status === BillwerkListStatus.Loading || match?.billwerk.contracts === BillwerkListStatus.Loading;
  useEffect(() => {
    if (!match || loading || pending || error) return;
    const lastPage = Math.max(0, Math.ceil(match.totalCount / PAGE_SIZE) - 1);
    if (page > lastPage) update({ page: lastPage > 0 ? String(lastPage) : null });
    // Clamp only after the current page and background Billwerk reads have completed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match, loading, pending, error, page]);

  // The Billwerk list loads in the background: ask again until it is there.
  useEffect(() => {
    if (pending) startPolling(POLL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [pending, startPolling, stopPolling]);

  // Each backend instance keeps its own list: ask for one loaded after this answer.
  const reload = () => setRefreshAfter(match?.billwerk.checkedAt ?? null);

  const title = translate("billwerkCustomerMatch.title");
  const classified = match !== undefined && status === BillwerkListStatus.Ready && !match.withheld;
  const reasons = Object.values(BillwerkCustomerMatchReason).filter(
    option => match && countOf(match, group, option) > 0
  );

  return (
    <Stack gap={2} data-testid="billwerk-customer-match">
      <Title title={title} />
      <Box>
        <Typography variant="overline" color="text.secondary">
          {translate("financeNotifications.section")}
        </Typography>
        <Typography component="h1" variant="h5" fontWeight={typography.fontWeight.semibold}>
          {title}
        </Typography>
        <Typography color="text.secondary">{translate("billwerkCustomerMatch.intro")}</Typography>
      </Box>
      {match && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            {match.billwerk.loadedAt && (
              <Typography color="text.secondary">
                {translate("billwerkCustomerMatch.loadedAt", {
                  time: formatCrmDateTime(match.billwerk.loadedAt),
                  count: formatCrmCount(match.billwerk.customerCount ?? 0),
                })}
              </Typography>
            )}
            <Typography color="text.secondary">
              {match.salesforceSync.lastSucceededAt
                ? translate("billwerkCustomerMatch.lastSync", {
                    time: formatCrmDateTime(match.salesforceSync.lastSucceededAt),
                  })
                : translate("billwerkCustomerMatch.noSync")}
            </Typography>
          </Box>
          {status !== BillwerkListStatus.NotConfigured && (
            <Button variant="outlined" onClick={reload} disabled={status === BillwerkListStatus.Loading}>
              {translate("billwerkCustomerMatch.reload")}
            </Button>
          )}
        </Stack>
      )}
      {error && <Alert severity="error">{translate("billwerkCustomerMatch.loadError")}</Alert>}
      {loading && !data && <CircularProgress aria-label={translate("billwerkCustomerMatch.loading")} />}
      {status === BillwerkListStatus.Loading && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          {translate("billwerkCustomerMatch.status.LOADING")}
        </Alert>
      )}
      {status === BillwerkListStatus.Unavailable && (
        <Alert severity="error">{translate("billwerkCustomerMatch.status.UNAVAILABLE")}</Alert>
      )}
      {status === BillwerkListStatus.NotConfigured && (
        <Alert severity="warning">{translate("billwerkCustomerMatch.status.NOT_CONFIGURED")}</Alert>
      )}
      {match?.withheld && (
        <Alert severity="warning">
          {match.salesforceSync.unreadableFields.length > 0
            ? translate("billwerkCustomerMatch.withheld", { fields: match.salesforceSync.unreadableFields.join(", ") })
            : translate("billwerkCustomerMatch.notCopied")}
        </Alert>
      )}
      {match && !match.withheld && match.salesforceSync.unreadableFields.length > 0 && (
        <Alert severity="info">
          {translate("billwerkCustomerMatch.unreadable", { fields: match.salesforceSync.unreadableFields.join(", ") })}
        </Alert>
      )}
      {classified &&
        (match.billwerk.contracts === BillwerkListStatus.Loading ||
          match.billwerk.contracts === BillwerkListStatus.Unavailable) && (
          <Alert severity="info">{translate(`billwerkCustomerMatch.contracts.${match.billwerk.contracts}`)}</Alert>
        )}
      {match && !match.withheld && (
        <>
          {classified ? (
            <>
              <Tabs
                value={group}
                onChange={(_event, next: BillwerkCustomerMatchGroup) =>
                  update({ group: next, reason: null, page: null })
                }
                aria-label={translate("billwerkCustomerMatch.groupFilter")}
                variant="scrollable"
              >
                {GROUPS.map(option => (
                  <Tab
                    key={option}
                    value={option}
                    label={translate("billwerkCustomerMatch.groupCount", {
                      group: translate(`billwerkCustomerMatch.groups.${option}`),
                      count: formatCrmCount(countOf(match, option)),
                    })}
                  />
                ))}
              </Tabs>
              {reasons.length > 1 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  role="group"
                  aria-label={translate("billwerkCustomerMatch.reasonFilter")}
                >
                  {[null, ...reasons].map(option => (
                    <Chip
                      key={option ?? "all"}
                      label={
                        option
                          ? `${translate(`billwerkCustomerMatch.reasons.${option}`)} (${formatCrmCount(
                              countOf(match, group, option)
                            )})`
                          : translate("billwerkCustomerMatch.allReasons")
                      }
                      color={option === reason ? "primary" : "default"}
                      variant={option === reason ? "filled" : "outlined"}
                      aria-pressed={option === reason}
                      onClick={() => update({ reason: option, page: null })}
                    />
                  ))}
                </Stack>
              )}
            </>
          ) : (
            <Typography component="h2" variant="h6">
              {translate("billwerkCustomerMatch.salesforceSide")}
            </Typography>
          )}
          <TextField
            label={translate("billwerkCustomerMatch.search")}
            helperText={translate("billwerkCustomerMatch.searchHint")}
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            size="small"
            sx={{ maxWidth: 420 }}
          />
          <TableContainer>
            <Table size="small" aria-label={title}>
              <TableHead>
                <TableRow>
                  <TableCell>{translate("billwerkCustomerMatch.columns.salesforce")}</TableCell>
                  <TableCell>{translate("billwerkCustomerMatch.columns.debitorAccount")}</TableCell>
                  <TableCell>{translate("billwerkCustomerMatch.columns.billwerk")}</TableCell>
                  <TableCell>{translate("billwerkCustomerMatch.columns.reason")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {match.rows.map((row, index) => (
                  <MatchRow
                    // Rows have no id of their own; the page and the filter decide them.
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    row={row}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {match.rows.length === 0 && (
            <Typography color="text.secondary">{translate("billwerkCustomerMatch.empty")}</Typography>
          )}
          {match.totalCount > PAGE_SIZE && (
            <ListPagination
              total={match.totalCount}
              page={page + 1}
              perPage={PAGE_SIZE}
              resource={ResourceName.BillwerkCustomerMatch}
              onPageChange={next => update({ page: next > 1 ? String(next - 1) : null })}
              isLoading={loading}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export const BillwerkCustomerMatchPage = () => (
  <RequirePermission permission={ResourceName.BillwerkCustomerMatch}>
    <BillwerkCustomerMatchView />
  </RequirePermission>
);
